import { CombinationsOptions, GlobalOptions } from '@/enums/global/GlobalOptions';
import i18n from '@/i18n';

export const combinationsConfiguratorSteps = [
    {
        name: 'Selection',
        description: i18n.t(
            'As the first step in configuring the units, select the positions that will make up the cabinet. With the configurator, you can choose 2 or 3 positions for the unit. If you need more positions, please contact us. If positions are already included in a unit, they will be marked.'
        ),
    },
    {
        name: 'Sequence',
        description: i18n.t('Choose the order of positions (from left to right).'),
    },
    {
        name: 'Size',
        description: i18n.t(
            'Select the total width of the combined cabinet. The total width must be equal to or at most equal to the sum of the widths of both positions.'
        ),
    },
    {
        name: 'Configuration',
        description: i18n.t(
            'Select the drive side and the guides between the assemblies. Be sure that the option to select a partition guide is only available when the heights of the positions are the same. If the heights are different, you only have the option to select single guides. Based on your selections, the code for the combined cabinet will be generated automatically.'
        ),
    },
];

const baseFields = {
    PRODUCT_ID: {
        pid: 'productId',
        label: 'Num.',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: 'table',
    },
    SISTEM_PID: {
        pid: CombinationsOptions.SISTEM_PID,
        label: 'System',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: 'table',
    },
    QUANTITY_PID: {
        pid: CombinationsOptions.QUANTITY_PID,
        label: 'Quantity',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: 'table',
    },
    WIDTH_PID: {
        pid: CombinationsOptions.WIDTH_PID,
        label: 'Width',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: 'table',
    },
    HEIGHT_PID: {
        pid: CombinationsOptions.HEIGHT_PID,
        label: 'Height',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: 'table',
    },
    POSITION_PID: {
        pid: CombinationsOptions.POSITION_PID,
        label: 'Position',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: 'table',
    },
    ASSEMBLY_POSITION_PID: {
        pid: CombinationsOptions.ASSEMBLY_POSITION_PID,
        label: 'Assembly Position',
        type: 'dropdown',
        active: true,
        shouldUpdateConfigurator: true,
        isReadOnly: false,
        segment: 'table',
        options: [
            { label: '1', value: '1' },
            { label: '2', value: '2' },
            { label: '3', value: '3' },
        ],
        step: 2,
    },
    DRIVE_PID: {
        pid: CombinationsOptions.DRIVE_PID,
        label: 'Drive',
        type: 'dropdown',
        active: true,
        shouldUpdateConfigurator: true,
        isReadOnly: false,
        segment: 'configurator',
    },
    TOTAL_WIDTH_PID: {
        pid: CombinationsOptions.TOTAL_WIDTH_PID,
        label: 'Total Width',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: false,
        segment: 'totalWidth',
    },
    COMBINATION_TYPE_PID: {
        pid: CombinationsOptions.COMBINATION_TYPE_PID,
        label: 'Combination type',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: 'configurator',
    },
    NUMBER_OF_COMBINATIONS_PID: {
        pid: CombinationsOptions.NUMBER_OF_COMBINATIONS_PID,
        label: 'Number of combinations',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
    },
    COMBINATIONS_PID: {
        pid: CombinationsOptions.COMBINATIONS_PID,
        label: 'Combinations',
        type: 'boolean',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
        value: true,
    },
    LEFT_GUIDE_PID: {
        pid: GlobalOptions.LEFT_GUIDE_PID,
        label: 'Left Guide',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: 'configurator',
        noChoiceValue: 'Brez',
    },
    RIGHT_GUIDE_PID: {
        pid: GlobalOptions.RIGHT_GUIDE_PID,
        label: 'Right Guide',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: 'configurator',
        noChoiceValue: 'Brez',
    },
    ROLLER_SHUTTER_BOX_SIZE_PID: {
        pid: CombinationsOptions.ROLLER_SHUTTER_BOX_SIZE_PID,
        label: 'Roller Shutter Box Size',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
    },
    SELECTED_ROLLER_SHUTTER_BOX_SIZE_PID: {
        pid: CombinationsOptions.SELECTED_ROLLER_SHUTTER_BOX_SIZE_PID,
        label: 'Selected Roller Shutter Box Size',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
    },
};

const shuttersFields = {
    ...baseFields,
};

const shuttersAlternativeFields = {
    ...baseFields,
    LEFT_GUIDE_PID: {
        pid: GlobalOptions.ALTERNATIVE_LEFT_GUIDE_PID,
        label: 'Left Guide',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: 'configurator',
        noChoiceValue: 'Brez',
    },
    RIGHT_GUIDE_PID: {
        pid: GlobalOptions.ALTERNATIVE_RIGHT_GUIDE_PID,
        label: 'Right Guide',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: 'configurator',
        noChoiceValue: 'Brez',
    },
};

const simpleFields = {
    ...baseFields,
    LEFT_GUIDE_PID: {
        pid: GlobalOptions.ALTERNATIVE_LEFT_GUIDE_PID,
        label: 'Left Guide',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: 'configurator',
        noChoiceValue: 'Brez',
    },
    RIGHT_GUIDE_PID: {
        pid: GlobalOptions.ALTERNATIVE_RIGHT_GUIDE_PID,
        label: 'Right Guide',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: 'configurator',
        noChoiceValue: 'Brez',
    },
    DRIVE_PID: {
        pid: CombinationsOptions.DRIVE_PID,
        label: 'Drive',
        type: 'dropdown',
        active: true,
        shouldUpdateConfigurator: false,
        isReadOnly: false,
        segment: 'configurator',
    },
    COMBINATION_TYPE_PID: {
        pid: CombinationsOptions.COMBINATION_TYPE_PID,
        label: 'Combination type',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: 'configurator',
    },
};

const heroFields = {
    ...baseFields,
    LEFT_GUIDE_PID: {
        pid: GlobalOptions.LEFT_GUIDE_PID,
        label: 'Left Guide',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: 'configurator',
        noChoiceValue: '',
    },
    RIGHT_GUIDE_PID: {
        pid: GlobalOptions.RIGHT_GUIDE_PID,
        label: 'Right Guide',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: 'configurator',
        noChoiceValue: '',
    },
    COMBINATIONS_PID: {
        pid: GlobalOptions.HERO_COMBINATIONS_PID,
        label: 'Hero Combinations',
        type: 'boolean',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
        value: true,
    },
    COMBINED_ASSEMBLY_PID: {
        pid: GlobalOptions.COMBINED_ASSEMBLY_PID,
        label: 'Combined Assembly',
        type: 'boolean',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
        value: true,
    },
    TOTAL_WIDTH_PID: {
        pid: GlobalOptions.HERO_TOTAL_WIDTH_PID,
        label: 'Total Width',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: false,
        segment: 'totalWidth',
    },
};

const heroAlternativeFields = {
    ...heroFields,
    FLOOR_MOUNTING_HERO_PID: {
        pid: GlobalOptions.FLOOR_MOUNTING_HERO_PID,
        label: 'Floor Mounting Hero',
        type: 'boolean',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
    },
    RIGHT_GUIDE_HOLDER_PID: {
        pid: GlobalOptions.RIGHT_GUIDE_HOLDER_PID,
        label: 'Right Guide Holder',
        type: 'boolean',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
    },
    LEFT_GUIDE_HOLDER_PID: {
        pid: GlobalOptions.LEFT_GUIDE_HOLDER_PID,
        label: 'Left Guide Holder',
        type: 'boolean',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
    },
    SLIDING_SCREEN: {
        pid: 'sliding-screen',
        label: '',
        type: 'boolean',
        active: true,
        shouldUpdateConfigurator: false,
        isReadOnly: false,
        segment: 'guides',
        step: 4,
    },
    ROLLER_SHUTTER_BOX_SIZE_PID: {
        pid: CombinationsOptions.CONSOLE_SIZE_PID,
        label: 'Roller Shutter Box Size',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
    },
    SELECTED_ROLLER_SHUTTER_BOX_SIZE_PID: {
        pid: CombinationsOptions.SELECTED_CONSOLE_SIZE_PID,
        label: 'Selected Roller Shutter Box Size',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
    },
};

export const getRequiredCombinationsChecklistFields = (configuratorType: string) => {
    console.log(configuratorType, 'configuratorType');
    switch (configuratorType) {
        case 'shutters':
            return shuttersFields;
        case 'shutters-alternative':
            return shuttersAlternativeFields;
        case 'simple':
            return simpleFields;
        case 'hero':
            return heroFields;
        case 'hero-alternative':
            return heroAlternativeFields;
        default:
            return shuttersFields;
    }
};
